interface WebMCPTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (input: Record<string, unknown>) => Promise<Record<string, unknown>>;
}

interface WebMCPModelContext {
  registerTool: (
    tool: WebMCPTool,
    options?: { signal?: AbortSignal },
  ) => Promise<void>;
}

declare global {
  interface Document {
    modelContext?: WebMCPModelContext;
  }
}

const APP_STORE_URL = 'https://apps.apple.com/us/app/id6499389695';

const navigationTool: WebMCPTool = {
  name: 'navigate_spatial_worlds',
  description: 'Navigate to a public Spatial Worlds page or landing page section.',
  inputSchema: {
    type: 'object',
    properties: {
      destination: {
        type: 'string',
        enum: ['home', 'features', 'gallery', 'pricing', 'reviews', 'support', 'privacy', 'terms'],
      },
    },
    required: ['destination'],
    additionalProperties: false,
  },
  execute: async ({ destination }) => {
    const paths: Record<string, string> = {
      home: '/',
      features: '/#features',
      gallery: '/#gallery',
      pricing: '/#pricing',
      reviews: '/#testimonials',
      support: '/support',
      privacy: '/privacy',
      terms: '/terms',
    };
    const path = typeof destination === 'string' ? paths[destination] : undefined;
    if (!path) {
      throw new Error('Unsupported Spatial Worlds destination.');
    }
    window.location.assign(path);
    return { destination, url: new URL(path, window.location.origin).toString() };
  },
};

const appStoreTool: WebMCPTool = {
  name: 'open_spatial_worlds_app_store',
  description: 'Open the official Spatial Worlds App Store listing.',
  inputSchema: {
    type: 'object',
    properties: {},
    additionalProperties: false,
  },
  execute: async () => {
    window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer');
    return { url: APP_STORE_URL };
  },
};

export function registerWebMCPTools(): () => void {
  const modelContext = document.modelContext;
  if (!modelContext?.registerTool) {
    return () => undefined;
  }

  const controller = new AbortController();
  void Promise.all([
    modelContext.registerTool(navigationTool, { signal: controller.signal }),
    modelContext.registerTool(appStoreTool, { signal: controller.signal }),
  ]).catch(() => {
    controller.abort();
  });

  return () => controller.abort();
}